import Vue from 'vue'

import {
  findAll,
  show,
  list,
  create,
  update,
  exclude,
  attach,
  generateAsaas,
  generateAsaasBatch,
  paymentLinkSuggestions,
  linkPayments,
  listSentHausefulInvoices,
} from '@/views/invoices/hauseful/invoiceService'

import { uploadToS3 } from '@/services/upload/uploadService'

/** Sequência das buscas da aba Pendentes; só a última resposta pode escrever na lista. */
let hausefulInvoicesRequestId = 0

/**
 * Liga o resultado de uma nota do lote de volta à linha que o usuário marcou. O backend devolve os
 * mesmos payments_ids que recebeu, então é por eles que se acha a imobiliária e a filial - o
 * financeiro precisa ver DE QUEM é a nota que falhou, não uma lista de ids.
 */
const describeSelectedInvoice = (selected, paymentsIds) => {
  const key = (paymentsIds ?? []).join(',')
  const found = selected.find((invoice) => invoice.key === key)

  return {
    realEstate: found?.realEstate ?? '',
    branch: found?.branch ?? null,
    total: found?.total ?? 0,
  }
}

const state = {
  hausefulInvoices: [],
  hausefulInvoiceData: {},
  hausefulInvoiceInfo: {},
  hausefulTakeRateInvoices: [],
  hausefulInvoiceFileLoading: false,
  hausefulInvoiceFileDeleting: false,
  paymentLinkSuggestions: [],
  // Notas marcadas na aba Pendentes para emissão em lote.
  selectedHausefulInvoices: [],
  // Desfecho de cada nota do último lote; só as que falharam interessam ao usuário.
  hausefulInvoiceBatchFailures: [],
}

const getters = {
  hausefulInvoices: (state) => state.hausefulInvoices,
  hausefulInvoiceData: (state) => state.hausefulInvoiceData,
  hausefulInvoiceInfo: (state) => state.hausefulInvoiceInfo,
  hausefulTakeRateInvoice: (state) => state.hausefulTakeRateInvoice,
  hausefulTakeRateInvoices: (state) => state.hausefulTakeRateInvoices,
  hausefulInvoiceFileLoading: (state) => state.hausefulInvoiceFileLoading,
  hausefulInvoiceFileDeleting: (state) => state.hausefulInvoiceFileDeleting,
  paymentLinkSuggestions: (state) => state.paymentLinkSuggestions,
  selectedHausefulInvoices: (state) => state.selectedHausefulInvoices,
  hausefulInvoiceBatchFailures: (state) => state.hausefulInvoiceBatchFailures,

  selectedHausefulInvoicesTotal: (state) =>
    state.selectedHausefulInvoices.reduce((total, { total: amount }) => total + Number(amount ?? 0), 0),
}

const actions = {
  async fetchHausefulInvoices({ commit, dispatch, rootState }) {
    // Intervalos diferentes demoram tempos MUITO diferentes nesta tela, então a resposta de uma
    // busca antiga pode chegar depois da nova e sobrescrever a lista com o período errado. Só a
    // última busca disparada pode escrever.
    const requestId = ++hausefulInvoicesRequestId

    commit('setIsLoading', true)

    const params = { ...rootState.filters.filters }

    try {
      const { data } = await findAll(params)
      const { invoices } = data

      if (requestId !== hausefulInvoicesRequestId) return
      commit('setHausefulInvoices', invoices)
    } catch ({ data }) {
      if (requestId !== hausefulInvoicesRequestId) return
      dispatch('showErrorMessage', data.message)
    } finally {
      // Uma busca obsoleta não pode apagar o loading da que ainda está em andamento.
      if (requestId === hausefulInvoicesRequestId) commit('setIsLoading', false)
    }
  },

  async fetchHausefulTakeRateInvoice({ commit, dispatch }, id) {
    commit('setIsLoading', true)

    try {
      const { data } = await show(id)
      commit('setHausefulTakeRateInvoice', data)
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async fetchHausefulTakeRateInvoices({ commit, dispatch, rootState }) {
    commit('setIsLoading', true)

    const defaultParams = {
      size: rootState.paginate.itemsPerPage,
      page: rootState.paginate.currentPage - 1,
    }

    const params = { ...defaultParams, ...rootState.filters.filters, ...{ sender: 'HAUSEFUL' } }

    try {
      const { data } = await list({ params })
      const { result, total } = data

      commit('setHausefulTakeRateInvoices', result)
      commit('setTotalPages', total)
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async deleteHausefulTakeRateInvoice({ commit, dispatch }, id) {
    commit('setIsLoading', true)

    try {
      await exclude({ id })
      commit('closeAllModals')
      commit('setHausefulInvoiceData', {})
      commit('setHausefulInvoiceInfo', {})
      dispatch('fetchHausefulInvoices')
      dispatch('fetchHausefulTakeRateInvoices')
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async generateAsaasHausefulInvoice({ commit, dispatch }) {
    commit('setIsLoading', true)

    try {
      await generateAsaas(state.hausefulInvoiceData.payments_ids)
      commit('closeAllModals')
      commit('setHausefulInvoiceData', {})
      commit('setHausefulInvoiceInfo', {})
      dispatch('fetchHausefulInvoices')
      dispatch('fetchHausefulTakeRateInvoices')
      dispatch('showSuccessMessage', Vue.i18n.translate('hausefulInvoices.modals.generateAsaas.success'))
    } catch (error) {
      const data = error?.data
      const message = data?.errors?.length >= 1
        ? data.errors[0].message
        : data?.message ?? Vue.i18n.translate('hausefulInvoices.modals.generateAsaas.error')
      dispatch('showErrorMessage', message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  /**
   * Emite em lote as notas marcadas na aba Pendentes.
   *
   * Fatiado em blocos de 20 porque é o limite do backend: o lote inteiro roda dentro de UMA
   * requisição e cada nota são três chamadas ao Asaas, então acima disso o risco é estourar timeout
   * de proxy - e aí ninguém saberia o que foi emitido.
   *
   * Sucesso parcial é o caso normal e não é erro: a nota que saiu já é documento fiscal. Por isso o
   * resultado é agregado e as falhas ficam listadas no modal, nota por nota, em vez de virarem um
   * toast genérico que apagaria o que deu certo.
   */
  async generateAsaasHausefulInvoicesBatch({ state, commit, dispatch }) {
    const selected = state.selectedHausefulInvoices
    if (!selected.length) return

    const CHUNK_SIZE = 20
    commit('setIsLoading', true)
    commit('setHausefulInvoiceBatchFailures', [])

    try {
      const results = []

      for (let index = 0; index < selected.length; index += CHUNK_SIZE) {
        const chunk = selected.slice(index, index + CHUNK_SIZE)
        const invoices = chunk.map(({ paymentsIds }) => ({ payments_ids: paymentsIds }))
        const { data } = await generateAsaasBatch(invoices)
        results.push(...(data?.results ?? []))
      }

      const failures = results
        .filter(({ emitted }) => !emitted)
        .map((result) => ({
          ...describeSelectedInvoice(selected, result.payments_ids),
          error: result.error,
        }))

      commit('setHausefulInvoiceBatchFailures', failures)
      commit('setSelectedHausefulInvoices', [])
      dispatch('fetchHausefulInvoices')
      dispatch('fetchHausefulTakeRateInvoices')

      const emitted = results.length - failures.length
      if (!failures.length) {
        commit('closeAllModals')
        dispatch('showSuccessMessage',
          Vue.i18n.translate('hausefulInvoices.modals.generateAsaasBatch.success', { count: emitted }))
      }
    } catch (error) {
      // Erro aqui é a chamada inteira falhando (rede, sessão, lote acima do limite), não uma nota.
      const data = error?.data
      const message = data?.errors?.length >= 1
        ? data.errors[0].message
        : data?.message ?? Vue.i18n.translate('hausefulInvoices.modals.generateAsaasBatch.error')
      dispatch('showErrorMessage', message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  // Nota emitida no painel do Asaas que o backend não conseguiu casar sozinha: o admin escolhe
  // a quinzena que ela cobriu.
  async fetchPaymentLinkSuggestions({ commit, dispatch }, invoiceId) {
    commit('setIsLoading', true)
    commit('setPaymentLinkSuggestions', [])

    try {
      const { data } = await paymentLinkSuggestions(invoiceId)
      commit('setPaymentLinkSuggestions', data)
    } catch (error) {
      dispatch('showErrorMessage', error?.data?.message
        ?? Vue.i18n.translate('hausefulInvoices.modals.linkPayments.error'))
    } finally {
      commit('setIsLoading', false)
    }
  },

  async linkHausefulInvoicePayments({ commit, dispatch }, { invoiceId, paymentsIds }) {
    commit('setIsLoading', true)

    try {
      await linkPayments(invoiceId, paymentsIds)
      commit('closeAllModals')
      commit('setPaymentLinkSuggestions', [])
      dispatch('fetchHausefulInvoices')
      dispatch('fetchHausefulTakeRateInvoices')
      dispatch('showSuccessMessage', Vue.i18n.translate('hausefulInvoices.modals.linkPayments.success'))
    } catch (error) {
      const data = error?.data
      const message = data?.errors?.length >= 1
        ? data.errors[0].message
        : data?.message ?? Vue.i18n.translate('hausefulInvoices.modals.linkPayments.error')
      dispatch('showErrorMessage', message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async attachHausefulTakeRateInvoiceFiles({ commit, dispatch }, file) {
    commit('setHausefulInvoiceFileLoading', true)

    const invoiceData = {
      ...{ payments_ids: state.hausefulInvoiceData.payments_ids },
      signed_object_url: {
        filename: file[0].name,
        filetype: file[0].type,
        folder_name: 'outputs',
      },
    }

    await create(invoiceData)
      .then(async ({ data }) => {
        try {
          await uploadToS3(data.signed_object_url.url, file[0])
          await attach({
            id: data.invoice_response.id,
            filename: file[0].name,
            external_filename: data.signed_object_url.external_filename,
          })
        } catch (error) {
          console.log(error)
          dispatch('showErrorMessage', error)
        }
      })
      .then(() => {
        commit('closeAllModals')
        commit('setHausefulInvoiceData', {})
        commit('setHausefulInvoiceInfo', {})
        dispatch('fetchHausefulInvoices')
        dispatch('fetchHausefulTakeRateInvoices')
        dispatch('showSuccessMessage', 'File attached successfully')
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setHausefulInvoiceFileLoading', false)
      })
  },

  async attachHausefulTakeRateInvoiceFilesUpdate({ commit, dispatch }, file) {
    commit('setHausefulInvoiceFileLoading', true)

    const invoiceData = {
      invoice_id: state.hausefulInvoiceInfo.id,
      signed_object_url: {
        filename: file[0].name,
        filetype: file[0].type,
        folder_name: 'outputs',
      },
    }

    update(invoiceData)
      .then(async ({ data }) => {
        try {
          await uploadToS3(data.url, file[0])
          await attach({
            id: state.hausefulInvoiceInfo.id,
            filename: file[0].name,
            external_filename: data.external_filename,
          })
        } catch (error) {
          dispatch('showErrorMessage', error)
          return
        }
      })
      .then(() => {
        commit('closeAllModals')
        commit('setHausefulInvoiceData', {})
        commit('setHausefulInvoiceInfo', {})
        dispatch('fetchHausefulInvoices')
        dispatch('fetchHausefulTakeRateInvoices')
        dispatch('showSuccessMessage', 'File updated successfully')
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setHausefulInvoiceFileLoading', false)
      })
  },
}

const mutations = {
  setHausefulInvoices: (state, hausefulInvoices) => {
    state.hausefulInvoices = hausefulInvoices
    // A lista trocou (filtro, recarga, emissão): manter marcada uma nota que saiu da tela emitiria
    // algo que o usuário não está mais vendo.
    state.selectedHausefulInvoices = []
  },

  setHausefulInvoiceData: (state, hausefulInvoice) => {
    state.hausefulInvoiceData = hausefulInvoice
  },

  setHausefulInvoiceInfo: (state, hausefulInvoiceInfo) => {
    state.hausefulInvoiceInfo = hausefulInvoiceInfo
  },

  setHausefulTakeRateInvoice: (state, hausefulTakeRateInvoice) => {
    state.hausefulTakeRateInvoice = hausefulTakeRateInvoice
  },

  setHausefulTakeRateInvoices: (state, hausefulTakeRateInvoices) => {
    state.hausefulTakeRateInvoices = hausefulTakeRateInvoices
  },

  setPaymentLinkSuggestions: (state, suggestions) => {
    state.paymentLinkSuggestions = suggestions
  },

  setSelectedHausefulInvoices: (state, invoices) => {
    state.selectedHausefulInvoices = invoices
  },

  toggleHausefulInvoiceSelection: (state, invoice) => {
    const alreadySelected = state.selectedHausefulInvoices.some(({ key }) => key === invoice.key)

    state.selectedHausefulInvoices = alreadySelected
      ? state.selectedHausefulInvoices.filter(({ key }) => key !== invoice.key)
      : [...state.selectedHausefulInvoices, invoice]
  },

  setHausefulInvoiceBatchFailures: (state, failures) => {
    state.hausefulInvoiceBatchFailures = failures
  },

  setHausefulInvoiceFileLoading: (state, status) => {
    state.hausefulInvoiceFileLoading = status
  },

  setHausefulInvoiceFileDeleting: (state, status) => {
    state.hausefulInvoiceFileDeleting = status
  },
}

export default {
  state,
  mutations,
  actions,
  getters,
}
